"use client";

import { useAuth } from "@clerk/nextjs";
import { VariantProps } from "class-variance-authority";
import { usePathname, useRouter } from "next/navigation";
import { KeyboardEvent, useEffect, useRef, useState } from "react";
import { PiArrowClockwise, PiCloudArrowUp, PiLinkSimple, PiLockKeyOpen, PiSubtitles } from "react-icons/pi";

import { Button } from "@/components/button/button";
import { Card } from "@/components/card/card";
import Input from "@/components/input/input";
import Tabs from "@/components/tabs/tabs";
import { toast } from "@/components/toast/use-toast";
import { PAGES } from "@/configs/pages.config";
import { isValidDownloadLink } from "@/forms/validator.form";
import { trackLandingUnsupportedLinkSubmitted } from "@/lib/analytics";
import { buildAuthPagePath, buildFilesRedirectPath } from "@/lib/auth-redirect";
import { DEFAULT_LOCALE, getLocaleFromPathname, stripLocaleFromPathname } from "@/lib/i18n/locales";
import { withSeoLocalePath } from "@/lib/i18n/seo-paths";
import { LandingHeroIntent } from "@/lib/landing-intent";
import { cn } from "@/lib/utils";

import { heroSectionVariants } from "./hero-section";

export type HeroUploadCopy = {
  linkTabLabel: string;
  linkPlaceholder: string;
  linkCtaLabel: string;
  uploadTabLabel: string;
  uploadDropzoneLabel: string;
  uploadDropzoneHint: string;
  uploadCtaLabel: string;
};

export type HeroUploadProps = {
  layout: VariantProps<typeof heroSectionVariants>["layout"];
  variant?: "button" | "intent-card";
  defaultIntent?: LandingHeroIntent;
  copy?: HeroUploadCopy;
};

const uploadLabel = {
  en: "Upload File",
  es: "Subir archivo",
  fr: "Televerser un fichier",
  de: "Datei hochladen",
  it: "Carica file",
  pt: "Enviar arquivo",
} as const;

const unsupportedLinkCopy = {
  en: {
    title: "Unsupported link",
    description: "Paste an Instagram Reel or TikTok URL here, or switch to Upload File for audio and video files.",
  },
  es: {
    title: "Enlace no compatible",
    description: "Pega aquí un enlace de Instagram Reel o TikTok, o cambia a Subir archivo para audio y video.",
  },
  fr: {
    title: "Lien non pris en charge",
    description: "Collez ici un lien Instagram Reel ou TikTok, ou passez à Téléverser un fichier pour l’audio et la vidéo.",
  },
  de: {
    title: "Link nicht unterstützt",
    description: "Füge hier einen Instagram-Reel- oder TikTok-Link ein oder wechsle zu Datei hochladen für Audio- und Videodateien.",
  },
  it: {
    title: "Link non supportato",
    description: "Incolla qui un link Instagram Reel o TikTok, oppure passa a Carica file per file audio e video.",
  },
  pt: {
    title: "Link não suportado",
    description: "Cole aqui um link de Instagram Reel ou TikTok, ou mude para Carregar ficheiro para áudio e vídeo.",
  },
} as const;

const DEFAULT_INTENT_COPY: HeroUploadCopy = {
  linkTabLabel: "Paste Link",
  linkPlaceholder: "Paste Instagram Reel or TikTok URL...",
  linkCtaLabel: "Generate Transcript",
  uploadTabLabel: "Upload File",
  uploadDropzoneLabel: "Drag & drop MP4, MOV, or audio files",
  uploadDropzoneHint: "Perfect for podcasts, meetings, voice notes, and downloaded videos.",
  uploadCtaLabel: "Transcribe File",
};

const INTENT_TAB_ORDER: LandingHeroIntent[] = ["link", "upload"];
const NAVIGATION_DELAY_MS = 160;

const pendingStateCopy = {
  en: {
    authTitle: "Create a free account to continue",
    authDescription: "We will take you to signup first, then open your workspace with your source ready to go.",
    workspaceTitle: "Opening your workspace",
    workspaceDescription: "Hang tight. We are taking you straight into the dashboard now.",
    linkPendingLabel: "Continuing...",
    uploadPendingLabel: "Opening...",
  },
  es: {
    authTitle: "Crea una cuenta gratis para continuar",
    authDescription: "Primero te llevaremos al registro y luego abriremos tu espacio de trabajo con tu fuente lista.",
    workspaceTitle: "Abriendo tu espacio de trabajo",
    workspaceDescription: "Espera un momento. Te llevamos directamente al panel ahora.",
    linkPendingLabel: "Continuando...",
    uploadPendingLabel: "Abriendo...",
  },
  fr: {
    authTitle: "Créez un compte gratuit pour continuer",
    authDescription: "Nous vous emmenons d'abord vers l'inscription, puis nous ouvrons votre espace de travail avec votre source prête.",
    workspaceTitle: "Ouverture de votre espace de travail",
    workspaceDescription: "Un instant. Nous vous emmenons directement dans le tableau de bord.",
    linkPendingLabel: "Continuation...",
    uploadPendingLabel: "Ouverture...",
  },
  de: {
    authTitle: "Erstelle ein kostenloses Konto, um fortzufahren",
    authDescription: "Wir bringen dich zuerst zur Registrierung und oeffnen danach deinen Workspace mit bereits vorbereiteter Quelle.",
    workspaceTitle: "Workspace wird geoeffnet",
    workspaceDescription: "Einen Moment. Wir bringen dich jetzt direkt ins Dashboard.",
    linkPendingLabel: "Weiter...",
    uploadPendingLabel: "Wird geoeffnet...",
  },
  it: {
    authTitle: "Crea un account gratuito per continuare",
    authDescription: "Ti portiamo prima alla registrazione e poi apriamo il tuo workspace con la fonte gia pronta.",
    workspaceTitle: "Apertura del workspace",
    workspaceDescription: "Un attimo. Ti stiamo portando direttamente nella dashboard.",
    linkPendingLabel: "Continuo...",
    uploadPendingLabel: "Apro...",
  },
  pt: {
    authTitle: "Cria uma conta gratuita para continuar",
    authDescription: "Primeiro levamos-te para o registo e depois abrimos o teu workspace com a tua fonte pronta.",
    workspaceTitle: "A abrir o teu workspace",
    workspaceDescription: "Um instante. Estamos a levar-te diretamente para o painel.",
    linkPendingLabel: "A continuar...",
    uploadPendingLabel: "A abrir...",
  },
} as const;

export default function HeroUpload({ layout, variant = "button", defaultIntent = "link", copy = DEFAULT_INTENT_COPY }: HeroUploadProps) {
  const { isSignedIn } = useAuth();
  const router = useRouter();
  const pathname = usePathname();
  const locale = pathname ? getLocaleFromPathname(pathname) : DEFAULT_LOCALE;
  const isHomePath = stripLocaleFromPathname(pathname ?? "/") === "/";
  const label = uploadLabel[locale] ?? uploadLabel[DEFAULT_LOCALE];
  const invalidLinkMessage = unsupportedLinkCopy[locale] ?? unsupportedLinkCopy[DEFAULT_LOCALE];
  const pendingCopy = pendingStateCopy[locale] ?? pendingStateCopy[DEFAULT_LOCALE];
  const [isMounted, setIsMounted] = useState(false);
  const [activeIntent, setActiveIntent] = useState<LandingHeroIntent>(defaultIntent);
  const [linkInput, setLinkInput] = useState("");
  const [pendingIntent, setPendingIntent] = useState<LandingHeroIntent | null>(null);
  const [pendingMode, setPendingMode] = useState<"auth" | "workspace" | null>(null);
  const navigationTimeoutRef = useRef<number | null>(null);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    setActiveIntent(defaultIntent);
  }, [defaultIntent]);

  useEffect(() => {
    return () => {
      if (navigationTimeoutRef.current) {
        window.clearTimeout(navigationTimeoutRef.current);
      }
    };
  }, []);

  const scheduleNavigation = (intent: LandingHeroIntent, destination: string, mode: "auth" | "workspace") => {
    if (navigationTimeoutRef.current) {
      window.clearTimeout(navigationTimeoutRef.current);
    }

    setPendingIntent(intent);
    setPendingMode(mode);

    navigationTimeoutRef.current = window.setTimeout(() => {
      router.push(destination);
    }, NAVIGATION_DELAY_MS);
  };

  if (variant === "button") {
    return (
      <div className={cn("@container w-full flex flex-col items-center", layout === "horizontal" && "lg:items-start")}>
        <div className="flex flex-wrap justify-center gap-4">
          <Button
            variant="outline"
            className="group h-12 w-fit"
            disabled={pendingIntent !== null}
            onClick={() => {
              const destination = !isSignedIn && isHomePath ? withSeoLocalePath(locale, PAGES.SIGN_UP.PATH) : PAGES.FILES.PATH;
              scheduleNavigation("upload", destination, !isSignedIn && isHomePath ? "auth" : "workspace");
            }}
          >
            {pendingIntent === "upload" ? (
              <PiArrowClockwise className="w-6 h-6 animate-spin" />
            ) : (
              <PiCloudArrowUp className="w-6 h-6 group-hover:animate-shake" />
            )}
            {pendingIntent === "upload" ? pendingCopy.uploadPendingLabel : label}
          </Button>
        </div>
      </div>
    );
  }

  const continueToWorkspace = (intent: LandingHeroIntent, url?: string) => {
    const shouldGoThroughSignup = !isSignedIn && isHomePath;
    const destination = shouldGoThroughSignup
      ? buildAuthPagePath(withSeoLocalePath(locale, PAGES.SIGN_UP.PATH), { fileUrl: intent === "link" ? url : undefined })
      : intent === "link"
        ? buildFilesRedirectPath({ fileUrl: url })
        : PAGES.FILES.PATH;

    scheduleNavigation(intent, destination, shouldGoThroughSignup ? "auth" : "workspace");
  };

  const handleLinkIntent = (value: string) => {
    const trimmed = value.trim();
    if (!trimmed) {
      continueToWorkspace("link");
      return;
    }

    if (!isValidDownloadLink(trimmed)) {
      if (isHomePath) {
        trackLandingUnsupportedLinkSubmitted(trimmed, {
          locale,
          pathname: pathname ?? undefined,
        });
      }

      toast({
        title: invalidLinkMessage.title,
        description: invalidLinkMessage.description,
        variant: "destructive",
      });
      return;
    }

    continueToWorkspace("link", trimmed);
  };

  const currentIntent = isMounted ? activeIntent : defaultIntent;
  const uploadDropzoneProps = {
    role: "button" as const,
    tabIndex: 0,
    onClick: () => continueToWorkspace("upload"),
    onKeyDown: (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        continueToWorkspace("upload");
      }
    },
  };

  return (
    <Card
      className={cn(
        "w-full max-w-2xl overflow-hidden rounded-[1.75rem] border-2 border-primary-400 bg-white/95 shadow-xl shadow-primary-300/30 backdrop-blur",
        layout === "horizontal" && "lg:max-w-xl lg:mx-0"
      )}
    >
      {!isMounted ? (
        <>
          <div className="grid grid-cols-3 gap-2 bg-gray-100 p-2">
            {INTENT_TAB_ORDER.map(intent => {
              const labelText = intent === "link" ? copy.linkTabLabel : copy.uploadTabLabel;
              const isActive = intent === currentIntent;

              return (
                <div
                  key={intent}
                  className={cn(
                    "rounded-2xl border px-3 py-3 text-center text-sm font-medium",
                    isActive ? "border-primary-300 bg-white text-primary" : "border-transparent text-foreground"
                  )}
                >
                  {labelText}
                </div>
              );
            })}
          </div>
          <div className="p-4 sm:p-5">
            {currentIntent === "upload" ? (
              <div className="gap-4">
                <div
                  {...uploadDropzoneProps}
                  className="rounded-[1.5rem] border border-dashed border-primary-300 bg-primary-50 px-5 py-8 text-center cursor-pointer transition-colors hover:border-primary hover:bg-primary-100/60 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
                >
                  <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-white shadow-sm">
                    <PiCloudArrowUp className="w-7 h-7 text-primary-400" />
                  </div>
                  <div className="text-base font-semibold text-black">{copy.uploadDropzoneLabel}</div>
                  <p className="mt-2 text-sm text-gray-600">{copy.uploadDropzoneHint}</p>
                </div>
                <Button variant="outline" className="mt-4 h-12 w-full" onClick={() => continueToWorkspace("upload")}>
                  <PiCloudArrowUp className="w-5 h-5" />
                  {copy.uploadCtaLabel}
                </Button>
              </div>
            ) : (
              <div className="gap-4">
                <Input
                  value=""
                  readOnly
                  placeholder={copy.linkPlaceholder}
                  className="h-12 rounded-2xl"
                  icon={<PiLinkSimple className="w-5 h-5 text-primary-400" />}
                />
                <Button className="mt-4 h-12 w-full">
                  <PiSubtitles className="w-5 h-5" />
                  {copy.linkCtaLabel}
                </Button>
              </div>
            )}
          </div>
        </>
      ) : (
        <Tabs value={activeIntent} onValueChange={value => setActiveIntent(value as LandingHeroIntent)} className="w-full">
          <Tabs.List className="grid grid-cols-2 gap-2 border-b-0 bg-gray-100 p-2">
            {INTENT_TAB_ORDER.map(intent => {
              const labelText = intent === "link" ? copy.linkTabLabel : copy.uploadTabLabel;

              return (
                <Tabs.Trigger
                  key={intent}
                  value={intent}
                  className="rounded-2xl border border-transparent px-3 py-3 text-sm font-medium data-[state=active]:border-primary-300 data-[state=active]:bg-white"
                >
                  {labelText}
                </Tabs.Trigger>
              );
            })}
          </Tabs.List>

          <div className="p-4 sm:p-5">
            <Tabs.Content value="link" className="gap-4">
              <Input
                value={linkInput}
                onChange={event => setLinkInput(event.target.value)}
                placeholder={copy.linkPlaceholder}
                className="h-12 rounded-2xl"
                icon={<PiLinkSimple className="w-5 h-5 text-primary-400" />}
                onKeyDown={event => {
                  if (event.key === "Enter") {
                    handleLinkIntent(linkInput);
                  }
                }}
              />
              <Button className="h-12 w-full" onClick={() => handleLinkIntent(linkInput)}>
                {pendingIntent === "link" ? <PiArrowClockwise className="w-5 h-5 animate-spin" /> : <PiSubtitles className="w-5 h-5" />}
                {pendingIntent === "link" ? pendingCopy.linkPendingLabel : copy.linkCtaLabel}
              </Button>
              {pendingIntent === "link" && pendingMode ? (
                <div className="mt-4 rounded-2xl border border-primary-200 bg-primary-50 px-4 py-3 text-left">
                  <div className="flex items-start gap-3">
                    <div className="mt-0.5 rounded-full bg-white p-2 text-primary shadow-sm">
                      {pendingMode === "auth" ? (
                        <PiLockKeyOpen className="h-4 w-4" />
                      ) : (
                        <PiArrowClockwise className="h-4 w-4 animate-spin" />
                      )}
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-black">
                        {pendingMode === "auth" ? pendingCopy.authTitle : pendingCopy.workspaceTitle}
                      </p>
                      <p className="mt-1 text-sm leading-6 text-gray-600">
                        {pendingMode === "auth" ? pendingCopy.authDescription : pendingCopy.workspaceDescription}
                      </p>
                    </div>
                  </div>
                </div>
              ) : null}
            </Tabs.Content>

            <Tabs.Content value="upload" className="gap-4">
              <div
                {...uploadDropzoneProps}
                className={cn(
                  "rounded-[1.5rem] border border-dashed border-primary-300 bg-primary-50 px-5 py-8 text-center cursor-pointer transition-colors hover:border-primary hover:bg-primary-100/60 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary",
                  pendingIntent === "upload" && "border-primary bg-primary-100/70"
                )}
              >
                <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-white shadow-sm">
                  {pendingIntent === "upload" ? (
                    <PiArrowClockwise className="w-7 h-7 animate-spin text-primary-400" />
                  ) : (
                    <PiCloudArrowUp className="w-7 h-7 text-primary-400" />
                  )}
                </div>
                <div className="text-base font-semibold text-black">{copy.uploadDropzoneLabel}</div>
                <p className="mt-2 text-sm text-gray-600">{copy.uploadDropzoneHint}</p>
              </div>
              <Button
                variant="outline"
                className="h-12 w-full"
                disabled={pendingIntent !== null}
                onClick={() => continueToWorkspace("upload")}
              >
                {pendingIntent === "upload" ? (
                  <PiArrowClockwise className="w-5 h-5 animate-spin" />
                ) : (
                  <PiCloudArrowUp className="w-5 h-5" />
                )}
                {pendingIntent === "upload" ? pendingCopy.uploadPendingLabel : copy.uploadCtaLabel}
              </Button>
              {pendingIntent === "upload" && pendingMode ? (
                <div className="rounded-2xl border border-primary-200 bg-primary-50 px-4 py-3 text-left">
                  <div className="flex items-start gap-3">
                    <div className="mt-0.5 rounded-full bg-white p-2 text-primary shadow-sm">
                      {pendingMode === "auth" ? (
                        <PiLockKeyOpen className="h-4 w-4" />
                      ) : (
                        <PiArrowClockwise className="h-4 w-4 animate-spin" />
                      )}
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-black">
                        {pendingMode === "auth" ? pendingCopy.authTitle : pendingCopy.workspaceTitle}
                      </p>
                      <p className="mt-1 text-sm leading-6 text-gray-600">
                        {pendingMode === "auth" ? pendingCopy.authDescription : pendingCopy.workspaceDescription}
                      </p>
                    </div>
                  </div>
                </div>
              ) : null}
            </Tabs.Content>
          </div>
        </Tabs>
      )}
    </Card>
  );
}
